import { Grid } from '@mui/material';
import { HSpacer, RoutalLoader, RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';

import DegradedBg from '../../../../assets/img/subscription/background-degraded-green.svg';
import CartIcon from '../../../../assets/img/subscription/cart.svg';
import InfoIcon from '../../../../assets/img/subscription/info.svg';
import ProjectIcon from '../../../../assets/img/subscription/projects.svg';
import VehicleIcon from '../../../../assets/svg/subscription/VehicleIcon';
import { currencyToSymbol, totalPriceWithDiscount } from '../../../../constants/Pricing';
import { BillingPeriod, PlanType } from '../../../../domain/subscription/Subscription';
import { Vehicle } from '../../../../domain/vehicle/Vehicle';
import { useRetrieveSubscriptionPricing } from '../../../../hooks/subscription/useRetrieveSubscriptionPricing';
import Text from '../../../../react-components/Text';
import { ProjectData } from './NewProjectForm';

const TextDivider = () => (
  <>
    <VSpacer medium />
    <div style={{ width: `100%`, height: `1px`, backgroundColor: `white` }} />
    <VSpacer medium />
  </>
);

type NewProjectSummaryProps = {
  step: number;
  subscriptionPlan: PlanType;
  billingPeriod: BillingPeriod;
  projectData: ProjectData;
  vehicles: Vehicle[];
};

export const NewProjectSummary = ({
  step,
  subscriptionPlan,
  billingPeriod,
  projectData,
  vehicles,
}: NewProjectSummaryProps) => {
  const { data: pricing, isLoading } = useRetrieveSubscriptionPricing({
    billingPeriod,
  });

  const getTotalPrice = () => {
    if (!pricing) return ``;

    let totalPrice = 0;

    if (vehicles.length > 0) {
      totalPrice += vehicles.length * pricing.vehicles[subscriptionPlan];

      if (projectData.traffic) {
        totalPrice += vehicles.length * pricing.traffic;
      }
    }

    totalPrice = totalPriceWithDiscount({
      price: totalPrice,
      amountOff: pricing.discount?.amount_off,
      percentOff: pricing.discount?.percent_off,
    });

    return `${totalPrice.toFixed(2)}${currencyToSymbol(pricing.currency)}`;
  };

  return (
    <Grid
      container
      item
      alignItems="center"
      justifyContent="center"
      style={{ background: `url(${DegradedBg}) no-repeat`, backgroundSize: `cover`, width: `100%` }}
    >
      <Grid container direction="column" style={{ maxWidth: `640px`, padding: `0 20px` }}>
        {isLoading ? (
          <Grid
            item
            container
            direction="column"
            style={{ height: `100%`, width: `100%` }}
            alignItems="center"
            justifyContent="center"
          >
            <RoutalLoader />
          </Grid>
        ) : (
          <>
            {pricing ? (
              <>
                <Grid item container direction="row">
                  <Grid item>
                    <img width="24" src={CartIcon} alt="" draggable="false" />
                  </Grid>

                  <HSpacer small />

                  <Grid item>
                    <Text variant="h6" style={{ color: `white` }} intlId="projects.create.summary.title" />
                  </Grid>
                </Grid>

                <VSpacer large />

                <Grid item container direction="row">
                  <Grid item xs={4}>
                    <Text style={{ color: `white` }} intlId="projects.create.summary.item" />
                  </Grid>

                  <Grid item xs={4} container justifyContent="center">
                    <Text style={{ color: `white` }} intlId="projects.create.summary.quantity" />
                  </Grid>

                  <Grid item xs={4} container justifyContent="flex-end">
                    <Text style={{ color: `white` }} intlId="projects.create.summary.price" />
                  </Grid>
                </Grid>

                <TextDivider />

                <Grid item container direction="row">
                  <Grid item xs={4} container alignItems="center">
                    <Grid item>
                      <img width="24" src={ProjectIcon} alt="" draggable="false" />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      <Text style={{ color: `white` }} intlId="projects.create.summary.projects" />
                    </Grid>
                  </Grid>

                  <Grid item xs={4} container alignItems="center" justifyContent="center">
                    <Text style={{ color: `white` }}>1</Text>
                  </Grid>

                  <Grid item xs={4} container alignItems="center" justifyContent="flex-end">
                    <Text style={{ color: `white` }}>{`${pricing.projects.toFixed(2)}€`}</Text>
                  </Grid>
                </Grid>

                {step > 0 && vehicles.length > 0 ? (
                  <>
                    <TextDivider />

                    <Grid item container direction="row">
                      <Grid item xs={4} container alignItems="center">
                        <Grid item display={`flex`}>
                          <VehicleIcon style={{ height: `24px`, color: `white` }} />
                        </Grid>

                        <HSpacer small />

                        <Grid item>
                          <Text style={{ color: `white` }} intlId="projects.create.summary.vehicles" />
                        </Grid>
                      </Grid>

                      <Grid item xs={4} container alignItems="center" justifyContent="center">
                        <Text style={{ color: `white` }}>{vehicles.length}</Text>
                      </Grid>

                      <Grid
                        item
                        xs={4}
                        container
                        direction="row"
                        wrap="nowrap"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <Grid item>
                          <Text tagName="span" style={{ color: `white` }}>{`${(
                            vehicles.length * pricing.vehicles[subscriptionPlan]
                          ).toFixed(2)}€`}</Text>
                        </Grid>

                        {projectData.traffic ? (
                          <Grid item container direction="row" style={{ width: `unset`, marginLeft: `4px` }}>
                            <Grid item container alignItems="center" style={{ width: `unset` }}>
                              <Text tagName="span" variant="body" style={{ color: `white` }}>
                                {`(+${(vehicles.length * pricing.traffic).toFixed(2)}€)`}
                              </Text>
                            </Grid>

                            <Grid item style={{ marginLeft: `4x` }}>
                              <RoutalTooltip
                                title={<Text intlId="projects.create.summary.traffic_price" />}
                                mouseEnterDelay={0.5}
                                mouseLeaveDelay={0}
                                backgroundColor={RoutalPalette.neutral00}
                              >
                                <img
                                  src={InfoIcon}
                                  alt=""
                                  height="16"
                                  width="16"
                                  draggable="false"
                                  style={{ marginTop: `6px` }}
                                />
                              </RoutalTooltip>
                            </Grid>
                          </Grid>
                        ) : null}
                      </Grid>
                    </Grid>
                  </>
                ) : null}

                <TextDivider />

                <Grid item container direction="row">
                  <Grid item xs={6}>
                    <Text isBold variant="h6" style={{ color: `white` }} intlId="projects.create.summary.total_price" />
                  </Grid>

                  <Grid item xs={6} container justifyContent="flex-end">
                    <Text isBold variant="h6" style={{ color: `white` }}>
                      {getTotalPrice()}
                    </Text>
                  </Grid>
                </Grid>
              </>
            ) : null}
          </>
        )}
      </Grid>
    </Grid>
  );
};
